// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

// material ui
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

// components
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

function StartTypeAvatar({ type }) {
  return (
    <Avatar
      variant="rounded"
      sx={{
        backgroundColor: getTypeColor(type) || '#555',
        borderRadius: 2,
        color: '#fff',
        fontWeight: 'bold',
        height: 32,
        width: 32,
        fontSize: 16,
      }}
    >
      <i className={`fas ${getTypeIcon(type) || 'fa-circle'}`}></i>
    </Avatar>
  );
}

function NodeLabel({ node, onSelect, searchValue, t }) {
  const labelText = node.name ?? `Node ${node.energyTreeNodeId}`;
  const childCount = node.children?.length || 0;
  const [isHovered, setIsHovered] = useState(false);

  // Check if this node matches the search
  const isMatch =
    searchValue && node.name?.toLowerCase().includes(searchValue.toLowerCase());

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ py: 0.5, pr: 0.5, minHeight: 36 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StartTypeAvatar type={node.type} />

      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: isMatch ? 600 : 500,
            color: isMatch ? 'success.main' : 'inherit',
          }}
        >
          {labelText}
        </Typography>
        {childCount > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: isMatch ? 'success.main' : 'text.secondary',
            }}
          >
            {childCount}{' '}
            {childCount === 1
              ? t('common.child', { ns: 'energyxplorer-analysis-tabs' })
              : t('common.children', { ns: 'energyxplorer-analysis-tabs' })}
          </Typography>
        )}
      </Box>

      {isHovered && (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(node);
          }}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          {t('actions.select', { ns: 'common' })}
        </Button>
      )}
    </Stack>
  );
}

// Helper function to build hierarchical tree structure from flat array
function buildHierarchicalTree(flatNodes) {
  if (!flatNodes?.length) return [];

  // Create a map of all nodes by ID
  const nodeMap = new Map();
  flatNodes.forEach((node) => {
    nodeMap.set(node.energyTreeNodeId, {
      ...node,
      children: [],
    });
  });

  // Build the tree structure
  const rootNodes = [];
  flatNodes.forEach((node) => {
    const nodeWithChildren = nodeMap.get(node.energyTreeNodeId);
    if (node.parentId && nodeMap.has(node.parentId)) {
      // Add to parent's children
      nodeMap.get(node.parentId).children.push(nodeWithChildren);
    } else {
      // Root node (parentId is null)
      rootNodes.push(nodeWithChildren);
    }
  });

  return rootNodes;
}

function renderTree({ node, onSelect, searchValue, t }) {
  return (
    <TreeItem
      key={node.energyTreeNodeId}
      itemId={String(node.energyTreeNodeId)}
      label={
        <NodeLabel
          node={node}
          onSelect={onSelect}
          searchValue={searchValue}
          t={t}
        />
      }
      sx={{
        '& .MuiTreeItem-content': {
          paddingY: 0,
        },
      }}
    >
      {Array.isArray(node.children) &&
        node.children.map((child) =>
          renderTree({ node: child, onSelect, searchValue, t }),
        )}
    </TreeItem>
  );
}

const ComparisonItemSelector = ({
  nodeOptions = [],
  selectedNode,
  onChange,
  onRemove,
  placeholder,
  showRemoveButton = false,
}) => {
  const { t } = useTranslation([
    'energyxplorer',
    'energyxplorer-analysis-tabs',
    'common',
  ]);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Build hierarchical tree structure
  const hierarchicalTree = useMemo(() => {
    return buildHierarchicalTree(nodeOptions);
  }, [nodeOptions]);

  // Get expanded items for search - when searching, expand nodes with matches
  const searchExpandedItems = useMemo(() => {
    if (!searchValue) return null;

    const expandedIds = new Set();

    const checkNodeAndChildren = (node) => {
      let hasMatch = false;

      // Check if current node matches
      if (node.name?.toLowerCase().includes(searchValue.toLowerCase())) {
        hasMatch = true;
      }

      // Check children recursively
      const childMatches =
        node.children?.some((child) => checkNodeAndChildren(child)) || false;

      if (hasMatch || childMatches) {
        expandedIds.add(String(node.energyTreeNodeId));
        return true;
      }

      return false;
    };

    hierarchicalTree.forEach((rootNode) => checkNodeAndChildren(rootNode));
    return Array.from(expandedIds);
  }, [hierarchicalTree, searchValue]);

  // Get the first root node's ID for default expansion
  const defaultExpanded = useMemo(() => {
    if (hierarchicalTree.length > 0) {
      return [String(hierarchicalTree[0].energyTreeNodeId)];
    }
    return [];
  }, [hierarchicalTree]);

  const handleClearSearch = () => {
    setSearchValue('');
  };

  const handleNodeSelect = (node) => {
    onChange(node);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchValue(''); // Clear search when dialog closes
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => setOpen(true)}
          fullWidth
          startIcon={
            selectedNode && <StartTypeAvatar type={selectedNode.type} />
          }
          endIcon={<UnfoldMoreRoundedIcon />}
          sx={{
            borderRadius: '6px',
            borderColor: 'divider',
            textTransform: 'none',

            justifyContent: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: 1,
              textAlign: 'left',
              overflow: 'hidden',
            }}
          >
            {selectedNode ? (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                  title={selectedNode.name}
                >
                  {selectedNode.name}
                </Typography>
              </>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 0.75 }}
              >
                {placeholder ||
                  t('nodes.comparison_selector.select_comparison_node')}
              </Typography>
            )}
          </Box>
        </Button>

        {showRemoveButton && (
          <IconButton
            onClick={onRemove}
            sx={{
              p: 1.2,
              borderRadius: '6px',
              ':hover': {
                backgroundColor: 'action.hover',
                borderColor: 'error.main',
                color: 'error.main',
              },
            }}
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
        )}
      </Box>

      <Dialog maxWidth="xl" open={open} onClose={handleClose}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">
              {t('nodes.comparison_selector.select_comparison_node')}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ minWidth: 600 }}>
            {/* Search input field */}
            <TextField
              fullWidth
              label={t('actions.search', { ns: 'common' })}
              size="small"
              variant="outlined"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  endAdornment: searchValue && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClearSearch}
                        edge="end"
                        size="small"
                        aria-label={t('actions.reset', { ns: 'common' })}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <SimpleTreeView
              key={searchValue ? `search-${searchValue}` : 'default'}
              aria-label="comparison-node-selector"
              defaultExpandedItems={searchExpandedItems || defaultExpanded}
            >
              {hierarchicalTree.map((rootNode) =>
                renderTree({
                  node: rootNode,
                  onSelect: handleNodeSelect,
                  t,
                  searchValue,
                }),
              )}
            </SimpleTreeView>
            {hierarchicalTree.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {t('nodes.comparison_selector.no_nodes_available')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ComparisonItemSelector;
